import { NetworkConnectionContext } from "@context-providers/network-connection/network-connection-context";
import { isInitialNetworkStatusOnline } from "@context-providers/network-connection/network-connection-provider-utils";
import { useToast } from "@hooks/use-toast";
import { PropsWithChildren, useEffect, useRef } from "react";

/** Wrapper component that provides network connection information to the application */
export function NetworkConnectionProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const { showToast } = useToast();

  // Use use ref instead of use state to avoid unnecessary re-renders
  const isOnline = useRef<boolean>(isInitialNetworkStatusOnline());

  /**
   * Listens to "online" and "offline" window events to know if the application lost or gained network connection.
   * It then sets the current connection status and shows a toast to inform the user about the status change.
   */
  useEffect(() => {
    function showOnlineToast(): void {
      showToast({
        message: "The application is back online.",
        type: "success",
        shouldAutoHide: true,
      });
    }

    function showOfflineToast(): void {
      showToast({
        message: "Network connection lost.",
        description:
          "The application will not work properly until the network connection is restored.",
        type: "error",
        shouldAutoHide: true,
      });
    }

    function handleConnectionStatusChange(isNewStatusOnline: boolean): void {
      // Early exit if the new status is the same as the current status
      if (isNewStatusOnline === isOnline.current) {
        return;
      }

      isOnline.current = isNewStatusOnline;
      isNewStatusOnline ? showOnlineToast() : showOfflineToast();
    }

    window.addEventListener("online", () => handleConnectionStatusChange(true));
    window.addEventListener("offline", () =>
      handleConnectionStatusChange(false)
    );

    return () => {
      window.removeEventListener("online", () =>
        handleConnectionStatusChange(true)
      );
      window.removeEventListener("offline", () =>
        handleConnectionStatusChange(false)
      );
    };
  // Since this useEffect creates event listeners, it should not listen to more dependencies
  }, [showToast]);

  return (
    <NetworkConnectionContext.Provider
      value={{
        isOnline: isOnline.current,
      }}
    >
      {children}
    </NetworkConnectionContext.Provider>
  );
}
